"use client";

import React, { useEffect, useRef, useState } from "react";
import { BookOpen, Calendar, Sparkles, ChartLine, GraduationCap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  num: "01",
  title: "Meet Your Teacher",
  desc: "Our friendly, native-speaking teachers are ready to welcome your little learner into a world of Spanish discovery.",
  icon: BookOpen,
  emphasized: false
}, {
  num: "02",
  title: "Join a Playful Class",
  desc: "Hop into a live, interactive classroom where every lesson is a new and exciting story waiting to be told.",
  icon: Calendar,
  emphasized: false
}, {
  num: "03",
  title: "Play & Learn",
  desc: "Jump into a world of games, songs, and colorful activities where learning Spanish feels like pure magic.",
  icon: Sparkles,
  emphasized: true
}, {
  num: "04",
  title: "Grow in Confidence",
  desc: "Watch as your child gains the confidence to speak Spanish naturally through encouraging conversations and laughter.",
  icon: ChartLine,
  emphasized: false
}, {
  num: "05",
  title: "Celebrate Every Step",
  desc: "Your child builds a portfolio of Spanish skills and a lifelong love for learning languages. Shine bright!",
  icon: GraduationCap,
  emphasized: false
}];
interface TimelineItemProps {
  step: typeof steps[0];
  index: number;
  "data-index": number;
  lineProgress: number;
}
function TimelineItem({
  step,
  index,
  "data-index": dataIndex,
  lineProgress
}: TimelineItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  const itemPosition = index / (steps.length - 1) * 100;
  const isActive = lineProgress >= itemPosition - 10;
  return <div data-section-id="96483" data-index={dataIndex} ref={ref} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-20 last:mb-0 ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="hidden md:block md:w-[45%]" />

      {}
      <div className="absolute left-6 md:left-1/2 top-0 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 z-10 flex flex-col items-center justify-center">
        <div className={`flex items-center justify-center rounded-full border-4 transition-all duration-500 ${step.emphasized ? "w-20 h-20 md:w-24 md:h-24 bg-background" : "w-12 h-12 md:w-16 md:h-16 bg-background"} ${isActive ? "border-primary shadow-[0_0_30px_rgba(255,165,0,0.4)]" : "border-muted shadow-none"}`}>
          <span className={`font-serif font-bold transition-colors duration-500 ${step.emphasized ? "text-2xl md:text-3xl" : "text-lg md:text-xl"} ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            {step.num}
          </span>
        </div>
      </div>

      {}
      <div className={`w-full pl-20 md:pl-0 md:w-[45%] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0 md:translate-x-0" : `opacity-0 translate-y-12 ${isEven ? "md:-translate-x-12" : "md:translate-x-12"}`}`}>
        <div className={`p-6 md:p-8 rounded-[2rem] border backdrop-blur-md transition-all duration-500 ${isActive ? "bg-card/90 border-primary/30 shadow-xl" : "bg-card/50 border-border/50 shadow-sm"} ${step.emphasized ? "ring-2 ring-primary/20" : ""}`}>
          <div className="flex items-center gap-4 mb-4">
            <div className={`p-3 rounded-2xl ${isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"} transition-colors duration-500`}>
              <step.icon className={step.emphasized ? "w-8 h-8" : "w-6 h-6"} />
            </div>
            <h3 className="text-xl md:text-2xl font-bold font-serif tracking-tight text-foreground">
              {step.title}
            </h3>
          </div>
          <p className="text-muted-foreground font-sans leading-relaxed text-sm md:text-base">
            {step.desc}
          </p>
        </div>
      </div>
    </div>;
}
export default function PathToSuccess() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startPoint = windowHeight / 2;
      const scrolledPastStart = startPoint - rect.top;
      const totalDistance = rect.height;
      let progress = scrolledPastStart / totalDistance * 100;
      progress = Math.max(0, Math.min(100, progress));
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="a-fun-learning-journey" ref={sectionRef} className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-32">
          <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4">
            A MAGICAL ADVENTURE
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-7xl font-bold font-serif tracking-tight mb-6">
            A Fun Learning <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Journey</span>
          </h3>
          <p className="text-xl text-muted-foreground font-sans">
            Where every lesson is a smile. Discover how we transform curious little learners into confident Spanish speakers through play and joy.
          </p>
        </div>

        {}
        <div className="max-w-5xl mx-auto relative" ref={timelineRef}>
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-1.5 bg-muted/30 -translate-x-1/2 rounded-full" />
          
          {}
          <div className="absolute left-6 md:left-1/2 top-0 w-1.5 bg-primary -translate-x-1/2 rounded-full transition-all duration-300 ease-out shadow-[0_0_15px_rgba(255,165,0,0.5)]" style={{
          height: `${scrollProgress}%`
        }} />

          {}
          <div className="relative py-10">
            {steps.map((step, i) => <TimelineItem key={i} data-index={i} index={i} step={step} lineProgress={scrollProgress} />)}
          </div>
        </div>

        {}
        <div className="mt-20 text-center flex justify-center">
          <Button size="lg" className="rounded-full px-10 py-8 text-xl font-bold bg-primary text-primary-foreground shadow-[0_8px_20px_rgba(249,115,22,0.3)] hover:shadow-[0_12px_30px_rgba(249,115,22,0.5)] hover:scale-105 transition-all duration-300 border-b-4 border-orange-700">
            Book a Free Trial
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
        </div>
      </div>
    </section>;
}